import { useEffect, useState } from "react";
import { api } from "../../lib/api";
import DashboardLayout from "../../components/DashboardLayout";
import { Shield, Building2, MessageSquare, AlertTriangle, CheckCircle2, RefreshCcw, Search, X } from "lucide-react";
import { toast } from "sonner";

const VERIFY_OPTIONS = [
  { v: "unverified", label: "Unverified" },
  { v: "domain_verified", label: "Domain verified" },
  { v: "platform_verified", label: "Platform verified ✓" },
];

export default function AdminModeration() {
  const [tab, setTab] = useState("orgs");
  return (
    <DashboardLayout>
      <div className="p-6 md:p-12">
        <div className="mb-6">
          <div className="label-eyebrow">Admin</div>
          <h1 className="font-heading text-4xl md:text-5xl uppercase tracking-tighter mt-2" data-testid="moderation-title">Moderation</h1>
          <p className="text-ink-muted mt-2 text-sm">Cross-platform tools to verify organisations, reassign campaigns, and moderate conversations.</p>
        </div>
        <div className="flex border-b border-line mb-6">
          {[
            { v: "orgs", icon: Building2, label: "Organisations" },
            { v: "convos", icon: MessageSquare, label: "Conversations" },
          ].map(t => (
            <button key={t.v} onClick={() => setTab(t.v)} data-testid={`tab-${t.v}`}
              className={`px-4 py-3 text-xs uppercase tracking-widest inline-flex items-center gap-2 border-b-2 ${tab === t.v ? "border-volt text-volt" : "border-transparent text-ink-muted hover:text-white"}`}>
              <t.icon className="w-3.5 h-3.5" /> {t.label}
            </button>
          ))}
        </div>
        {tab === "orgs" && <OrgsTab />}
        {tab === "convos" && <ConversationsTab />}
      </div>
    </DashboardLayout>
  );
}

function OrgsTab() {
  const [orgs, setOrgs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [q, setQ] = useState("");
  const [selected, setSelected] = useState(null);

  const load = () => {
    setLoading(true);
    api.get(`/admin/orgs${q ? `?q=${encodeURIComponent(q)}` : ""}`)
      .then(({ data }) => setOrgs(data))
      .catch(e => toast.error(e.response?.data?.detail || "Failed"))
      .finally(() => setLoading(false));
  };
  useEffect(() => { load(); /* eslint-disable-next-line */ }, []);

  return (
    <>
      <div className="flex gap-2 mb-4" data-testid="orgs-search">
        <input value={q} onChange={e => setQ(e.target.value)} onKeyDown={e => e.key === "Enter" && load()}
          placeholder="Search by org name or domain" data-testid="orgs-search-input"
          className="flex-1 bg-bg border border-line focus:border-volt outline-none px-3 py-2 text-white text-sm" />
        <button onClick={load} className="btn-ghost-line text-sm inline-flex items-center gap-2" data-testid="orgs-search-btn">
          <Search className="w-4 h-4" /> Search
        </button>
      </div>

      {loading ? <div className="text-ink-muted text-sm">Loading…</div> : (
        <div className="surface overflow-x-auto">
          <table className="w-full text-sm">
            <thead className="bg-bg-elev"><tr>
              <th className="text-left px-4 py-3 label-eyebrow">Organisation</th>
              <th className="text-left px-4 py-3 label-eyebrow">Type</th>
              <th className="text-left px-4 py-3 label-eyebrow">Plan</th>
              <th className="text-left px-4 py-3 label-eyebrow">Members</th>
              <th className="text-left px-4 py-3 label-eyebrow">Campaigns</th>
              <th className="text-left px-4 py-3 label-eyebrow">Verification</th>
              <th className="text-left px-4 py-3"></th>
            </tr></thead>
            <tbody className="divide-y divide-line">
              {orgs.map(o => (
                <tr key={o.id} data-testid={`org-row-${o.id}`} className="hover:bg-bg-elev/40">
                  <td className="px-4 py-3 font-medium">{o.organisation_name}</td>
                  <td className="px-4 py-3 text-ink-muted text-xs uppercase tracking-widest">{o.type}</td>
                  <td className="px-4 py-3 text-volt font-mono text-xs">{o.plan || "free"}</td>
                  <td className="px-4 py-3 font-mono">{o.member_count}</td>
                  <td className="px-4 py-3 font-mono">{o.campaign_count}</td>
                  <td className="px-4 py-3">
                    <VerifyChip status={o.verification_status} />
                  </td>
                  <td className="px-4 py-3 text-right">
                    <button onClick={() => setSelected(o)} data-testid={`open-org-${o.id}`}
                      className="text-xs uppercase tracking-widest text-volt hover:underline">Open →</button>
                  </td>
                </tr>
              ))}
              {orgs.length === 0 && <tr><td colSpan={7} className="px-4 py-10 text-center text-ink-muted">No organisations found.</td></tr>}
            </tbody>
          </table>
        </div>
      )}
      {selected && <OrgDetailDrawer org={selected} onClose={() => setSelected(null)} onChanged={() => { setSelected(null); load(); }} />}
    </>
  );
}

function VerifyChip({ status }) {
  const map = {
    unverified: { c: "border-line text-ink-muted", label: "Unverified" },
    domain_verified: { c: "border-volt/50 text-volt/90", label: "Domain ✓" },
    platform_verified: { c: "border-volt text-volt", label: "Platform ✓" },
  };
  const m = map[status] || map.unverified;
  return <span className={`text-[10px] uppercase tracking-widest border ${m.c} px-2 py-0.5`}>{m.label}</span>;
}

function OrgDetailDrawer({ org, onClose, onChanged }) {
  const [data, setData] = useState(null);
  const [verif, setVerif] = useState(org.verification_status || "unverified");
  const [verifNote, setVerifNote] = useState("");
  const [reassignCampaignId, setReassignCampaignId] = useState("");
  const [reassignTo, setReassignTo] = useState("");

  useEffect(() => {
    api.get(`/admin/orgs/${org.id}`).then(({ data }) => setData(data));
  }, [org.id]);

  const setVerification = async () => {
    try {
      await api.patch(`/admin/orgs/${org.id}/verify`, { verification_status: verif, note: verifNote || null });
      toast.success("Verification updated");
      onChanged();
    } catch (e) { toast.error(e.response?.data?.detail || "Failed"); }
  };

  const reassign = async () => {
    if (!reassignCampaignId || !reassignTo) return toast.error("Pick a campaign and new owner");
    try {
      await api.post(`/admin/campaigns/${reassignCampaignId}/reassign`, { new_owner_user_id: reassignTo });
      toast.success("Campaign reassigned");
      setReassignCampaignId(""); setReassignTo("");
      const { data } = await api.get(`/admin/orgs/${org.id}`); setData(data);
    } catch (e) { toast.error(e.response?.data?.detail || "Failed"); }
  };

  return (
    <div className="fixed inset-0 bg-black/80 backdrop-blur-sm z-50 flex justify-end" onClick={onClose}>
      <div className="bg-bg border-l border-line w-full max-w-2xl overflow-y-auto" onClick={e => e.stopPropagation()} data-testid="org-drawer">
        <div className="p-6 border-b border-line flex items-start justify-between">
          <div>
            <div className="label-eyebrow">Organisation</div>
            <h2 className="font-heading text-2xl uppercase tracking-tighter mt-2" data-testid="org-drawer-name">{org.organisation_name}</h2>
            <div className="flex flex-wrap gap-2 mt-2">
              <span className="text-[10px] uppercase tracking-widest text-ink-muted">{org.type}</span>
              <span className="text-[10px] uppercase tracking-widest text-volt">{org.plan || "free"}</span>
              <VerifyChip status={org.verification_status} />
              {(org.trusted_domains || []).map(d => (
                <span key={d} className="text-[10px] uppercase tracking-widest border border-line px-2 py-0.5">{d}</span>
              ))}
            </div>
          </div>
          <button onClick={onClose} data-testid="org-drawer-close"><X className="w-5 h-5" /></button>
        </div>

        <div className="p-6 space-y-8">
          {/* Verification */}
          <section data-testid="verify-section">
            <div className="label-eyebrow flex items-center gap-2"><Shield className="w-3 h-3" /> Verification</div>
            <div className="mt-3 grid grid-cols-2 gap-2">
              <select value={verif} onChange={e => setVerif(e.target.value)} data-testid="verify-select"
                className="bg-bg border border-line focus:border-volt outline-none px-3 py-2 text-white text-sm">
                {VERIFY_OPTIONS.map(v => <option key={v.v} value={v.v}>{v.label}</option>)}
              </select>
              <button onClick={setVerification} className="btn-volt text-sm" data-testid="verify-save-btn">Update verification</button>
            </div>
            <textarea value={verifNote} onChange={e => setVerifNote(e.target.value)} rows={2}
              placeholder="Internal note (optional)" data-testid="verify-note"
              className="mt-2 w-full bg-bg border border-line focus:border-volt outline-none px-3 py-2 text-white text-sm" />
          </section>

          {/* Members */}
          {data?.members && (
            <section data-testid="members-section">
              <div className="label-eyebrow">Members ({data.members.length})</div>
              <div className="mt-2 divide-y divide-line">
                {data.members.map(m => (
                  <div key={m.id} className="py-2 flex items-center justify-between gap-3">
                    <div className="min-w-0">
                      <div className="text-sm truncate">{m.full_name || m.email}</div>
                      <div className="text-[10px] uppercase tracking-widest text-ink-muted">{m.email} · {m.org_role}</div>
                    </div>
                    {m.email_verified && <CheckCircle2 className="w-4 h-4 text-volt shrink-0" />}
                  </div>
                ))}
              </div>
            </section>
          )}

          {/* Reassign campaign */}
          {data?.campaigns && data.campaigns.length > 0 && (
            <section data-testid="reassign-section">
              <div className="label-eyebrow flex items-center gap-2"><RefreshCcw className="w-3 h-3" /> Reassign campaign ownership</div>
              <p className="text-xs text-ink-muted mt-2">Force-transfer ownership of any campaign in this org. Existing owners become managers. Use sparingly.</p>
              <div className="mt-3 grid grid-cols-1 gap-2">
                <select value={reassignCampaignId} onChange={e => setReassignCampaignId(e.target.value)} data-testid="reassign-campaign-select"
                  className="bg-bg border border-line focus:border-volt outline-none px-3 py-2 text-white text-sm">
                  <option value="">Pick a campaign…</option>
                  {data.campaigns.map(c => (
                    <option key={c.id} value={c.id}>{c.name} ({c.status})</option>
                  ))}
                </select>
                <select value={reassignTo} onChange={e => setReassignTo(e.target.value)} data-testid="reassign-user-select"
                  className="bg-bg border border-line focus:border-volt outline-none px-3 py-2 text-white text-sm">
                  <option value="">Pick new owner…</option>
                  {data.members.map(m => (
                    <option key={m.id} value={m.id}>{m.full_name || m.email}</option>
                  ))}
                </select>
                <button onClick={reassign} disabled={!reassignCampaignId || !reassignTo} data-testid="reassign-btn"
                  className="btn-volt text-sm disabled:opacity-40">Reassign ownership</button>
              </div>
            </section>
          )}
        </div>
      </div>
    </div>
  );
}

function ConversationsTab() {
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [flaggedOnly, setFlaggedOnly] = useState(false);
  const [q, setQ] = useState("");

  const load = () => {
    setLoading(true);
    const params = new URLSearchParams();
    if (flaggedOnly) params.append("flagged_only", "true");
    if (q.trim()) params.append("q", q.trim());
    api.get(`/admin/conversations?${params.toString()}`)
      .then(({ data }) => setRows(data))
      .catch(e => toast.error(e.response?.data?.detail || "Failed"))
      .finally(() => setLoading(false));
  };
  useEffect(() => { load(); /* eslint-disable-next-line */ }, [flaggedOnly]);

  const moderate = async (id, action) => {
    const reason = (action === "flag" || action === "close") ? (window.prompt("Reason (optional):") || "") : null;
    try {
      await api.patch(`/admin/conversations/${id}/moderate`, { action, reason });
      toast.success(`Conversation ${action === "flag" ? "flagged" : action === "unflag" ? "unflagged" : action + "d"}`);
      load();
    } catch (e) { toast.error(e.response?.data?.detail || "Failed"); }
  };

  return (
    <>
      <div className="flex flex-wrap gap-3 items-center mb-4" data-testid="convo-filters">
        <input value={q} onChange={e => setQ(e.target.value)} onKeyDown={e => e.key === "Enter" && load()}
          placeholder="Search by subject" data-testid="convo-search-input"
          className="flex-1 min-w-[200px] bg-bg border border-line focus:border-volt outline-none px-3 py-2 text-white text-sm" />
        <label className="flex items-center gap-2 text-xs text-ink-muted">
          <input type="checkbox" checked={flaggedOnly} onChange={e => setFlaggedOnly(e.target.checked)} data-testid="flagged-only-toggle" />
          Flagged only
        </label>
        <button onClick={load} className="btn-ghost-line text-sm" data-testid="convo-search-btn">Search</button>
      </div>

      {loading ? <div className="text-ink-muted text-sm">Loading…</div> : (
        <div className="surface divide-y divide-line">
          {rows.map(c => (
            <div key={c.id} className="p-4 flex items-start justify-between gap-3" data-testid={`convo-row-${c.id}`}>
              <div className="min-w-0">
                <div className="flex items-center gap-2 flex-wrap">
                  <span className="text-sm font-medium truncate">{c.subject || "(no subject)"}</span>
                  {c.flagged && <span className="text-[10px] uppercase tracking-widest border border-[#FF3B30] text-[#FF3B30] px-2 py-0.5 inline-flex items-center gap-1"><AlertTriangle className="w-2.5 h-2.5" /> Flagged</span>}
                  <span className="text-[10px] uppercase tracking-widest text-ink-muted">{c.status} · {c.message_count} msg</span>
                </div>
                <div className="text-xs text-ink-muted mt-1">
                  <span className="text-white">{c.brand_name}</span> → <span className="text-white">{c.athlete_name}</span>
                </div>
              </div>
              <div className="flex gap-2 shrink-0">
                {!c.flagged ? <button onClick={() => moderate(c.id, "flag")} data-testid={`flag-${c.id}`} className="text-xs uppercase tracking-widest border border-[#FF3B30]/40 text-[#FF3B30] px-2 py-1 hover:bg-[#FF3B30]/10">Flag</button>
                  : <button onClick={() => moderate(c.id, "unflag")} data-testid={`unflag-${c.id}`} className="text-xs uppercase tracking-widest border border-line px-2 py-1 hover:bg-bg-elev">Unflag</button>}
                {c.status !== "closed" ? <button onClick={() => moderate(c.id, "close")} data-testid={`close-${c.id}`} className="text-xs uppercase tracking-widest border border-line px-2 py-1 hover:bg-bg-elev">Close</button>
                  : <button onClick={() => moderate(c.id, "reopen")} data-testid={`reopen-${c.id}`} className="text-xs uppercase tracking-widest border border-line px-2 py-1 hover:bg-bg-elev">Reopen</button>}
              </div>
            </div>
          ))}
          {rows.length === 0 && <div className="px-4 py-10 text-center text-ink-muted text-sm">No conversations match.</div>}
        </div>
      )}
    </>
  );
}
